import {
	TypographyCaps,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone-esm'
import { useEffect, useState } from 'react'
import UploadImageIcon from '../icons/Actions/UploadImageIcon'
import UploadArrow from '../icons/Arrows/UploadArrow'
import { Button } from '../buttons'

const ImageInputField = ({
	name,
	id,
	helper,
	uploadDescription,
	aspectRatio = 1,
	isValid,
	isError,
	isInactive = false,
	icon,
	additionalClasses = '',
	containerWidth = 'w-full',
	displayRequired,
	displayName = true,
	lines = 1,
	value = undefined,
	onChange = () => {},
}: {
	name: string
	id?: string
	helper?: string
	uploadDescription?: string
	aspectRatio?: number
	placeholder?: string
	isValid?: boolean
	isError?: boolean
	isInactive?: boolean
	icon?: React.ReactNode
	additionalClasses?: string
	containerWidth?: string
	displayRequired?: boolean
	displayName?: boolean
	lines?: number
	value?: string
	onChange?: (e: any) => void
}) => {
	const [imageToRender, setImageToRender] = useState(
		value && value !== undefined && value !== '' ? value : undefined,
	)

	useEffect(() => {
		if (imageToRender === undefined && value !== undefined && value !== '') {
			setImageToRender(value)
		}
	}, [value, imageToRender])

	const { getRootProps, getInputProps, open } = useDropzone({
		accept: {
			'image/jpeg': [],
			'image/png': [],
			'image/gif': [],
			'image/svg': [],
		},
		noClick: true,
		onDrop: acceptedFiles => {
			if (acceptedFiles.length) {
				const fileToRender = acceptedFiles[acceptedFiles.length - 1]
				setImageToRender(getURLForFile(fileToRender))
				onChange(fileToRender)
			}
		},
	})

	const getURLForFile = (file: File) => {
		return URL.createObjectURL(file)
	}

	return (
		<div className={containerWidth}>
			{displayName && (
				<label
					htmlFor={name}
					className="block leading-6 text-grey400 dark:text-grey500"
				>
					<TypographyCaps size="small">{`${name}${displayRequired ? ' *' : ''}`}</TypographyCaps>
				</label>
			)}
			{helper && (
				<TypographyParagraph
					size="medium"
					additionalClasses={clsx(
						'mt-1',
						isError
							? 'text-red'
							: 'text-textPrimaryLight dark:text-textPrimaryDark',
					)}
				>
					{helper}
				</TypographyParagraph>
			)}
			<div
				{...getRootProps({
					className: clsx(
						'dropzone group relative mt-2 w-full flex border rounded-lg hover:transition-all ease-in-out hover:duration-300',
						isError
							? 'border-red'
							: 'border-textInactiveLight dark:border-textInactiveDark hover:border-textPrimaryLight hover:dark:border-textPrimaryDark',
					),
				})}
			>
				<div
					className={clsx('container m-2 flex', isError ? 'border-red' : '')}
				>
					<div>
						<input {...getInputProps()} />
						<div
							className="relative h-[104px] cursor-pointer"
							onClick={() => open()}
						>
							<div
								style={{ width: `${aspectRatio * 104}px` }}
								className={clsx(
									`absolute h-[104px] w-[${aspectRatio * 104}px] flex items-center justify-center rounded-lg border border-textInactiveLight object-contain text-textPrimaryLight ease-in-out group-hover:border-textPrimaryLight group-hover:transition-all group-hover:duration-300 dark:border-textInactiveDark dark:text-textPrimaryDark group-hover:dark:border-textPrimaryDark`,
								)}
							>
									<UploadImageIcon className={clsx(imageToRender ? 'hidden' : 'block')} />
							</div>
							<img
								src={imageToRender}
								style={{ width: `${aspectRatio * 104}px` }}
								className={clsx(
									'transition-all duration-300 ease-in-out',
									imageToRender
										? 'opacity-100 group-hover:opacity-50'
										: 'opacity-0',
									`h-[104px] w-[${aspectRatio * 104}px] rounded-lg border border-textInactiveLight object-cover group-hover:border-textPrimaryLight dark:border-textInactiveDark group-hover:dark:border-textPrimaryDark`,
								)}
								alt={`${name}-image-input`}
							/>
						</div>
					</div>
					<div className="ml-6 flex flex-col items-start justify-center">
						{/* <div className="flex items-center justify-center text-textPrimaryLight dark:text-textPrimaryDark"> */}
						<Button variant="tertiary" onClick={() => open()} leftIcon={<UploadArrow />} >
								Browse files
						</Button>
						{/* </div> */}
						<TypographyParagraph
							additionalClasses={
								'text-textSecondaryLight dark:text-textSecondaryDark'
							}
						>
							{uploadDescription || ''}
						</TypographyParagraph>
					</div>
				</div>
				<div
					className={clsx(
						'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3',
						isValid &&
							'text-green group-focus:text-textPrimaryLight dark:group-focus:text-textPrimaryDark',
						isError &&
							'text-red group-focus:text-textPrimaryLight dark:group-focus:text-textPrimaryDark',
						isInactive && 'text-textInactiveLight dark:text-textInactiveDark',
					)}
				>
					{icon}
				</div>
			</div>
		</div>
	)
}

export default ImageInputField
